import { MatterBannerKind } from '@/matter/store/schemas'
import { useCallback, useContext, useEffect, useState } from 'react'
import { AuthContext } from '@/context/auth-context'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { getProviderFolderUrl } from '@/user_files/external-storage/ProviderMeta'
import { useExternalFilesTab } from '@/user_files/external-storage/hooks/useExternalFilesTab'
import { useSyncedFileSelection } from '@/user_files/external-storage/hooks/useSyncedFileSelection'
import type { DriveListSort } from '@/user_files/sorting'
import { useDriveSyncSetupBanner } from '@/user_files/external-storage/hooks/useDriveSyncSetupBanner'
import { readDriveSourcePreference, writeDriveSourcePreference } from '@/user_files/drive/utils/drive-source-preference-storage'
import type { DriveSourceViewMode } from '@/user_files/drive/types'

/**
 * Restores and remembers the Matter Files source per viewer and matter. initialViewMode overrides
 * the saved preference on entry; sourceResolutionPending keeps cold DMS discovery behind a neutral
 * loader. Also owns the shared DMS browse state and the "Open in {Provider}" target.
 */
export function useDriveSourceView(matterId: string | undefined, sort?: DriveListSort, initialViewMode?: DriveSourceViewMode) {
  const { user } = useContext(AuthContext)
  const userId = user?.uid
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const [sourceChoice, setSourceChoice] = useState(() => {
    const mode = initialViewMode ?? readDriveSourcePreference(userId, matterId) ?? 'matter'
    return { mode, restoring: mode === 'external' }
  })
  const [externalFolderTarget, setExternalFolderTarget] = useState<string | null>(null)

  const {
    activeExternalMapping,
    browseProps: externalBrowseProps,
    browsedFolderId,
    dmsDiscoveryStatus,
    initialSyncInProgress,
    syncedProvider,
  } = useExternalFilesTab({
    matterId: matterId ?? '',
    open: !!matterId,
    active: sourceChoice.mode === 'external',
    fallbackBreadcrumbItems: [],
    initialFolderIdOverride: externalFolderTarget,
    sort,
  })

  // A connected account with no folder mapped is still a source the user can switch to: it shows
  // the "connect a folder" empty state rather than hiding the DMS behind an invisible toggle.
  const { kind: driveSyncSetupKind, loading: driveSyncLoading, connectedAccount } = useDriveSyncSetupBanner(matterId)
  const isUnsynced = driveSyncSetupKind === MatterBannerKind.DriveSyncConnectFolder
  const showSourceToggle = dmsDiscoveryStatus === 'available' || dmsDiscoveryStatus === 'error' || isUnsynced
  // Null in the error state, where DmsSourceToggle shows its own error affordance instead of a logo.
  const dmsProvider = syncedProvider ?? (isUnsynced ? connectedAccount?.provider ?? null : null)

  const sourceStatusLoading = driveSyncLoading || dmsDiscoveryStatus === 'loading'
  const sourceResolutionPending = sourceChoice.restoring && driveSyncEnabled && sourceStatusLoading
  const shouldFallBack =
    sourceChoice.mode === 'external' &&
    (!driveSyncEnabled || (!sourceStatusLoading && (sourceChoice.restoring ? dmsDiscoveryStatus !== 'error' && !activeExternalMapping : !showSourceToggle)))
  // Resolve invalid sources in render; an effect alone can paint DMS before falling back.
  const viewMode = shouldFallBack ? 'matter' : sourceChoice.mode

  const syncedSelection = useSyncedFileSelection(browsedFolderId)

  const externalFolderUrl = syncedProvider && activeExternalMapping ? getProviderFolderUrl(syncedProvider, activeExternalMapping.external_folder_id) : undefined
  const initialSyncFailed = Boolean(activeExternalMapping && activeExternalMapping.last_synced_at === null && activeExternalMapping.failure_message)
  const driveExternalBrowseProps =
    initialSyncFailed && !externalBrowseProps.blockingError
      ? {
          ...externalBrowseProps,
          blockingError: 'Sync paused due to an error. We’ll retry automatically.',
          onRetry: undefined,
        }
      : externalBrowseProps

  // Using the toggle by hand always means "show me this source from the top", so any folder a
  // search result had aimed at is forgotten.
  const changeViewMode = useCallback(
    (mode: DriveSourceViewMode) => {
      writeDriveSourcePreference(userId, matterId, mode)
      setExternalFolderTarget(null)
      setSourceChoice({ mode, restoring: false })
    },
    [userId, matterId]
  )

  /**
   * Opens a synced folder in the DMS source view. Search spans both sources, so a synced folder can
   * be found from the matter view — where its contents are hidden, making it a dead end without this.
   *
   * Which mechanism works depends on where we are. Coming from the matter view, the browse hook
   * resets its folder as the view opens, so the target has to arrive as its initial folder. Already
   * in the DMS view there is no such reset — and no state change to trigger one — so the target has
   * to be an ordinary navigation instead.
   */
  const openExternalFolder = useCallback(
    (folderId: string) => {
      writeDriveSourcePreference(userId, matterId, 'external')
      setSourceChoice({ mode: 'external', restoring: false })
      // Recorded in both branches so the override and the visible folder never disagree; it is
      // inert while the browse hook already has a folder.
      setExternalFolderTarget(folderId)

      if (viewMode === 'external') {
        externalBrowseProps.onNavigateToFolder(folderId)
      }
    },
    [userId, matterId, viewMode, externalBrowseProps]
  )

  useEffect(() => {
    if (shouldFallBack) {
      setSourceChoice({ mode: 'matter', restoring: false })
      setExternalFolderTarget(null)
    } else if (sourceChoice.restoring && !sourceResolutionPending) {
      setSourceChoice({ ...sourceChoice, restoring: false })
    }
  }, [shouldFallBack, sourceChoice, sourceResolutionPending])

  return {
    viewMode,
    sourceResolutionPending,
    setViewMode: changeViewMode,
    openExternalFolder,
    showSourceToggle,
    dmsProvider,
    isUnsynced,
    syncedProvider,
    initialSyncInProgress,
    externalBrowseProps: driveExternalBrowseProps,
    syncedSelection,
    externalFolderUrl,
  }
}
